import { createSlice } from '@reduxjs/toolkit';

// Helper function to get initial state for a specific page
const getInitialPageState = () => ({
  // Pagination state
  currentPage: 1,
  totalRecords: 0,
  pageSize: 10,
  
  // Search and filter state
  search: null,
  searchText: "",
  sortValue: "recentlyAdded",
  sortType: "-createdAt",
  
  // Filter data
  filterData: null,
  isFilterUsing: false,
  onfilterPagination: true,
  
  // Selected items
  selectedItems: [],
  selectedRowKeys: [],
  
  // Column and UI state
  columnsvisible: [],
  visibleFilter: false,
  visibleSort: false,
  dropdownVisible: false,
  
  // Archive state
  isArchiveView: false,
  archiveMode: "archive",
  archiveTargetIds: [],
  
  // Other filter states
  selectedOption: "",
  selectedCategory: null,
  selectedType: "Calendar",
  date: null,
  selectedDate: null,
  updateDate: null,
  
  // Column management
  hidecolumn: [],
  hideRow: [],
  prefilledarray: [],
  columnData: null,
  
  // Refresh and loading states
  refreshToggle: false,
  togglerefresh: false,
  loadingTable: false,
  
  // Transfer and bulk operations
  transferData: [],
  bulkDeleteModal: false,
  archiveModalVisible: false,
  archiveLoading: false,
  
  // Previous states for navigation
  prevData: [],
  oldId: "",
  previousType: null,
  
  // Row data
  rowID: null,
  rowuserName: null,
  data: [],
  
  // Checked items
  checkedKeys: [],
  
  // Column order
  hasFetchedColumnOrder: false,
  columns: [],
  
  // Other states
  colId: null,
  selectedData: {},
  profileId: "",
  name: "",
  reloadState: false,
  numValues: 1,
  values: [""],
  yesNoValue: null,
  gateValue: null,
  transferModal: false,
  
  // Custom filters specific to each page
  customFilters: {}
});

const initialState = {
  // Global state for all pages
  global: {
    // User preferences
    userPreferences: {
      theme: 'light',
      language: 'en',
      timezone: 'UTC'
    },
    
    // Navigation state
    navigation: {
      previousRoute: null,
      currentRoute: null,
      routeHistory: []
    },
    
    // Global loading states
    loading: {
      global: false,
      api: false
    },
    
    // Global notifications
    notifications: [],
    
    // Global modals
    modals: {
      globalModal: false,
      confirmModal: false
    }
  },
  
  // Page-specific states
  pages: {
    // MobilisationManagement
    mobilisationManagement: getInitialPageState(),
    
    // CampaignManagement
    campaignManagement: getInitialPageState(),
    
    // UserManagement
    userManagement: getInitialPageState(),
    
    // SubscriptionManagement
    subscriptionManagement: getInitialPageState(),
    
    // NewsFeed
    newsFeed: getInitialPageState(),
    
    // Archive
    archive: getInitialPageState(),
    
    // Search
    search: getInitialPageState(),
    
    // Profile
    profile: getInitialPageState(),
    
    // Chat
    chat: getInitialPageState(),
    
    // JobAdvert
    jobAdvert: getInitialPageState(),
    
    // Add more pages as needed
    default: getInitialPageState()
  }
};

const globalStateSlice = createSlice({
  name: 'globalState',
  initialState,
  reducers: {
    // Global actions
    setGlobalLoading: (state, action) => {
      state.global.loading.global = action.payload;
    },
    setApiLoading: (state, action) => {
      state.global.loading.api = action.payload;
    },
    setCurrentRoute: (state, action) => {
      state.global.navigation.previousRoute = state.global.navigation.currentRoute;
      state.global.navigation.currentRoute = action.payload;
      state.global.navigation.routeHistory.push(action.payload);
    },
    addNotification: (state, action) => {
      state.global.notifications.push(action.payload);
    },
    removeNotification: (state, action) => {
      state.global.notifications = state.global.notifications.filter(
        (_, index) => index !== action.payload
      );
    },
    setGlobalModal: (state, action) => {
      state.global.modals.globalModal = action.payload;
    },
    setConfirmModal: (state, action) => {
      state.global.modals.confirmModal = action.payload;
    },
    
    // Page-specific actions (generic)
    setPageState: (state, action) => {
      const { pageName, key, value } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName][key] = value;
      }
    },
    
    setPageMultipleStates: (state, action) => {
      const { pageName, states } = action.payload;
      if (state.pages[pageName]) {
        Object.assign(state.pages[pageName], states);
      }
    },
    
    // Pagination actions
    setCurrentPage: (state, action) => {
      const { pageName, currentPage } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].currentPage = currentPage;
      }
    },
    setTotalRecords: (state, action) => {
      const { pageName, totalRecords } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].totalRecords = totalRecords;
      }
    },
    setPageSize: (state, action) => {
      const { pageName, pageSize } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].pageSize = pageSize;
      }
    },
    
    // Search and filter actions
    setSearch: (state, action) => {
      const { pageName, search } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].search = search;
      }
    },
    setSearchText: (state, action) => {
      const { pageName, searchText } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].searchText = searchText;
      }
    },
    setSortValue: (state, action) => {
      const { pageName, sortValue } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].sortValue = sortValue;
      }
    },
    setSortType: (state, action) => {
      const { pageName, sortType } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].sortType = sortType;
      }
    },
    
    // Filter data actions
    setFilterData: (state, action) => {
      const { pageName, filterData } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].filterData = filterData;
      }
    },
    setIsFilterUsing: (state, action) => {
      const { pageName, isFilterUsing } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].isFilterUsing = isFilterUsing;
      }
    },
    setOnfilterPagination: (state, action) => {
      const { pageName, onfilterPagination } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].onfilterPagination = onfilterPagination;
      }
    },
    
    // Selected items actions
    setSelectedItems: (state, action) => {
      const { pageName, selectedItems } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].selectedItems = selectedItems;
      }
    },
    setSelectedRowKeys: (state, action) => {
      const { pageName, selectedRowKeys } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].selectedRowKeys = selectedRowKeys;
      }
    },
    
    // Column and UI actions
    setColumnsvisible: (state, action) => {
      const { pageName, columnsvisible } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].columnsvisible = columnsvisible;
      }
    },
    setVisibleFilter: (state, action) => {
      const { pageName, visibleFilter } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].visibleFilter = visibleFilter;
      }
    },
    setVisibleSort: (state, action) => {
      const { pageName, visibleSort } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].visibleSort = visibleSort;
      }
    },
    setDropdownVisible: (state, action) => {
      const { pageName, dropdownVisible } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].dropdownVisible = dropdownVisible;
      }
    },
    
    // Archive actions
    setIsArchiveView: (state, action) => {
      const { pageName, isArchiveView } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].isArchiveView = isArchiveView;
      }
    },
    setArchiveMode: (state, action) => {
      const { pageName, archiveMode } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].archiveMode = archiveMode;
      }
    },
    setArchiveTargetIds: (state, action) => {
      const { pageName, archiveTargetIds } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].archiveTargetIds = archiveTargetIds;
      }
    },
    
    // Data actions
    setData: (state, action) => {
      const { pageName, data } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].data = data;
      }
    },
    setLoadingTable: (state, action) => {
      const { pageName, loadingTable } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].loadingTable = loadingTable;
      }
    },
    
    // Custom filters actions
    setCustomFilter: (state, action) => {
      const { pageName, filterKey, filterValue } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].customFilters[filterKey] = filterValue;
      }
    },
    setCustomFilters: (state, action) => {
      const { pageName, customFilters } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].customFilters = { ...state.pages[pageName].customFilters, ...customFilters };
      }
    },
    
    // Reset actions
    resetPageState: (state, action) => {
      const { pageName } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName] = getInitialPageState();
      }
    },
    
    resetPageFilters: (state, action) => {
      const { pageName } = action.payload;
      if (state.pages[pageName]) {
        state.pages[pageName].currentPage = 1;
        state.pages[pageName].search = null;
        state.pages[pageName].searchText = "";
        state.pages[pageName].sortValue = "recentlyAdded";
        state.pages[pageName].sortType = "-createdAt";
        state.pages[pageName].isFilterUsing = false;
        state.pages[pageName].selectedItems = [];
        state.pages[pageName].selectedRowKeys = [];
        state.pages[pageName].visibleFilter = false;
        state.pages[pageName].visibleSort = false;
        state.pages[pageName].dropdownVisible = false;
        state.pages[pageName].isArchiveView = false;
        state.pages[pageName].archiveMode = "archive";
        state.pages[pageName].archiveTargetIds = [];
        state.pages[pageName].selectedOption = "";
        state.pages[pageName].selectedCategory = null;
        state.pages[pageName].date = null;
        state.pages[pageName].selectedDate = null;
        state.pages[pageName].updateDate = null;
        state.pages[pageName].hidecolumn = [];
        state.pages[pageName].hideRow = [];
        state.pages[pageName].prefilledarray = [];
        state.pages[pageName].checkedKeys = [];
        state.pages[pageName].customFilters = {};
      }
    },
    
    resetAllStates: (state) => {
      return initialState;
    }
  }
});

export const {
  // Global actions
  setGlobalLoading,
  setApiLoading,
  setCurrentRoute,
  addNotification,
  removeNotification,
  setGlobalModal,
  setConfirmModal,
  
  // Page-specific actions
  setPageState,
  setPageMultipleStates,
  setCurrentPage,
  setTotalRecords,
  setPageSize,
  setSearch,
  setSearchText,
  setSortValue,
  setSortType,
  setFilterData,
  setIsFilterUsing,
  setOnfilterPagination,
  setSelectedItems,
  setSelectedRowKeys,
  setColumnsvisible,
  setVisibleFilter,
  setVisibleSort,
  setDropdownVisible,
  setIsArchiveView,
  setArchiveMode,
  setArchiveTargetIds,
  setData,
  setLoadingTable,
  setCustomFilter,
  setCustomFilters,
  resetPageState,
  resetPageFilters,
  resetAllStates
} = globalStateSlice.actions;

export default globalStateSlice.reducer;
